
import axios from 'axios'
import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import GoogleMapLoader from '@/components/GoogleMapLoader.vue'
import MapPlaceholder from '@/components/MapPlaceholder.vue'
import ProjectCardPlaceholder from '@/components/explore/ProjectCardPlaceholder.vue'
import { ExploreModule } from '@/store/modules/explore'
import { TGenericObject } from '@/types/base'
import { IPageSettings } from '@/types/cms'
import { IExploreProjectMinimal, ILocation, IProjectCategory, IProjectExploreFilters } from '@/types/projects'
import { API_URLS } from '@/utils/helpers'
import { IPublicShowcasePage } from '@/types/showcase'
import ShowcaseProjectCard from './ShowcaseProjectCard.vue'

@Component({
  components: {
    GoogleMapLoader,
    ProjectCardPlaceholder,
    MapPlaceholder,
    ShowcaseProjectCard
  },
  name: 'project-explore'
})
export default class ShowcaseProjectExplore extends Vue {
  @Prop({ default: 32 }) size!: number
  @Prop({ default: false }) listOnly
  @Prop({ required: true }) showcasePage!: IPublicShowcasePage
  @Prop() textBefore: string

  loading = false
  currentPage = 1
  projectCount = 1
  pageSize = 9
  categories: IProjectCategory[] = []
  projects: IExploreProjectMinimal[] = []
  projectLocations: ILocation[] = []
  showCategoryFilter = false
  showExpiredFilter = true

  projectFilters: IProjectExploreFilters = {
    searchString: '',
    hideFullyFunded: false,
    hideExpired: false,
    categories: [],
    sortOrder: null,
    cmsFilter: null,
    isShowcase: true
  }

  pageSettings: IPageSettings = null
  exploreHTML: string = null
  mapBoundsInitial = true
  googleMapsAccepted = false

  mapBounds = {
    lat: {
      min: null,
      max: null
    },
    lng: {
      min: null,
      max: null
    }
  }

  sortOptions = [
    { value: null, text: this.$gettext('Sort') },
    { value: 'title', text: this.$gettext('A-Z') },
    { value: '-title', text: this.$gettext('Z-A') },
    { value: '-created_at', text: this.$gettext('Newest') },
    { value: 'created_at', text: this.$gettext('Oldest') },
    { value: 'balance', text: this.$gettext('Low balance') },
    { value: '-balance', text: this.$gettext('High balance') },
    { value: 'random', text: this.$gettext('Random') }
  ]

  get useShowcasePageColor () {
    const color = this.showcasePage.primary_color
    return {
      'background-color': color,
      border: color
    }
  }

  @Watch('projectFilters', { deep: true })
  onFilterChange (): void {
    this.currentPage = 1
    this.fetchProjects(false)
    this.fetchLocations()
    this.mapBoundsInitial = true
  }

  @Watch('mapBounds', { deep: true })
  mapBoundsChange (): void {
    if (this.mapBoundsInitial) {
      this.mapBoundsInitial = false
      return
    }
    this.currentPage = 1
    this.fetchProjects()
  }

  @Watch('currentPage')
  onPageChange (): void {
    this.fetchProjects(true, true)
  }

  onBoundsChanged (bounds: google.maps.LatLngBounds | undefined): void {
    if (bounds) {
      const northEast = bounds.getNorthEast()
      const southWest = bounds.getSouthWest()
      this.mapBounds.lat.min = southWest.lat()
      this.mapBounds.lat.max = northEast.lat() < 0 ? 180 : northEast.lat()
      this.mapBounds.lng.min = southWest.lng()
      this.mapBounds.lng.max = northEast.lng() < 0 ? 180 : northEast.lng()
    }
  }

  get minMaxBounds (): google.maps.LatLngBoundsLiteral | undefined {
    if (this.projectLocations.length) {
      return {
        north: Math.max(...this.projectLocations.map(location => location.latitude)),
        east: Math.max(...this.projectLocations.map(location => location.longitude)),
        south: Math.min(...this.projectLocations.map(location => location.latitude)),
        west: Math.min(...this.projectLocations.map(location => location.longitude))
      }
    }
  }

  get exploreProjectsUrl (): string {
    return this.exploreUrl(API_URLS.EXPLORE.PROJECTS.EXPLORE, true)
  }

  get exploreProjectsUrlNoBounds (): string {
    return this.exploreUrl(API_URLS.EXPLORE.PROJECTS.EXPLORE, false)
  }

  get exploreLocationsUrl (): string {
    return this.exploreUrl(API_URLS.EXPLORE.PROJECTS.LOCATIONS, false, false)
  }

  exploreUrl (rootUrl: string, includeBounds = true, paginated = true): string {
    const filtersParams: TGenericObject = {}

    if (this.projectFilters.searchString) filtersParams.search = this.projectFilters.searchString
    if (this.projectFilters.hideExpired) filtersParams.hide_expired = this.projectFilters.hideExpired
    if (this.projectFilters.hideFullyFunded) filtersParams.hide_fully_funded = this.projectFilters.hideFullyFunded
    if (this.projectFilters.categories.length) filtersParams.categories = this.projectFilters.categories.join(',')
    if (this.projectFilters.sortOrder) filtersParams.ordering = this.projectFilters.sortOrder
    if (this.pageSlug) filtersParams.cms_filter = this.pageSlug
    filtersParams.is_showcase = true
    filtersParams.showcase_page_slug = this.showcasePage.slug

    if (includeBounds) {
      if (this.mapBounds.lat.min) filtersParams.latitude_min = this.mapBounds.lat.min
      if (this.mapBounds.lat.max) filtersParams.latitude_max = this.mapBounds.lat.max
      if (this.mapBounds.lng.min) filtersParams.longitude_min = this.mapBounds.lng.min
      if (this.mapBounds.lng.max) filtersParams.longitude_max = this.mapBounds.lng.max
    }

    const filterKeys = Object.keys(filtersParams)
    const urlFilterParams = filterKeys.length ? `&${filterKeys.map(key => `${key}=${filtersParams[key]}`).join('&')}` : ''
    return paginated ? `${rootUrl}?page_size=${this.pageSize}&page=${this.currentPage}${urlFilterParams}` : `${rootUrl}?${urlFilterParams}`
  }

  toggleCategory (category: IProjectCategory): void {
    if (this.projectFilters.categories.includes(category.slug)) {
      this.projectFilters.categories.forEach((item, index) => {
        if (item === category.slug) {
          this.projectFilters.categories.splice(index, 1)
        }
      })
    } else {
      this.projectFilters.categories.push(category.slug)
    }
  }

  toggleOffAllCategories (): void {
    this.projectFilters.categories = []
  }

  async fetchCategories (): Promise<void> {
    axios.get(API_URLS.EXPLORE.PROJECTS.CATEGORIES).then(response => {
      this.categories = response.data
    })
  }

  async fetchLocations (): Promise<void> {
    if (!this.pageSettings.showMap) {
      return
    }
    await axios.get(this.exploreLocationsUrl).then(response => {
      this.projectLocations = response.data
    })
  }

  async fetchProjects (useBounds = true, append = false): Promise<void> {
    if (this.projects.length) {
      this.loading = true
    } else {
      this.$wait.start('load projects')
    }

    await axios.get(useBounds ? this.exploreProjectsUrl : this.exploreProjectsUrlNoBounds).then((response) => {
      this.projectCount = response.data.count
      this.projects = append ? this.projects.concat(response.data.results) : response.data.results
    })

    this.loading = false
    this.$wait.end('load projects')
  }

  async loadData (): Promise<void> {
    this.fetchProjects()
    this.fetchLocations()
    this.fetchCategories()
  }

  get pageSlug () {
    return 'showcase'
  }

  async created (): Promise<void> {
    this.$wait.start('load data')

    const fetchContext = {
      slug: this.pageSlug
    }

    ExploreModule.fetchPageSettings(fetchContext).then(() => {
      this.pageSettings = ExploreModule.pageSettingsMap[this.pageSlug]

      this.projectFilters.sortOrder = this.pageSettings.defaultSortOrder

      this.loadData()
      this.$wait.end('load data')
    })

    if (this.$cookies.get('cookie_rule_detail')?.includes('google-maps')) {
      this.googleMapsAccepted = true
    }

    if (document.getElementById('explore-html')) {
      this.exploreHTML = document.getElementById('explore-html').innerHTML
    }
  }
}
